import { useEffect, useState } from "react";
import api, { errMsg, formatTL, formatDate, formatNumber, API } from "@/lib/api";
import PageHeader from "@/components/app/PageHeader";
import { Plus, Pencil, Trash2, X, Upload, Download, FileSpreadsheet, ChevronDown, ChevronUp, Fuel as FuelIcon } from "lucide-react";
import { toast } from "sonner";

const monthLabel = (m) => {
  if (!m || m === "unknown") return "Belirsiz";
  const [y, mo] = m.split("-");
  const names = ["Ocak","Şubat","Mart","Nisan","Mayıs","Haziran","Temmuz","Ağustos","Eylül","Ekim","Kasım","Aralık"];
  return `${names[parseInt(mo, 10) - 1] || mo} ${y}`;
};

const emptyRow = () => ({
  date: new Date().toISOString().slice(0,10),
  station: "", vehicle_id: "", liters: "", unit_price: "", total: "",
});

export default function FuelPage() {
  const [months, setMonths] = useState([]);
  const [grand, setGrand] = useState({});
  const [vehicles, setVehicles] = useState([]);
  const [expanded, setExpanded] = useState(new Set());
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyRow());
  const [showBulk, setShowBulk] = useState(false);
  const [bulkRows, setBulkRows] = useState([emptyRow()]);

  const load = async () => {
    try {
      const [m, v] = await Promise.all([
        api.get("/fuel-expenses/by-month"),
        api.get("/vehicles"),
      ]);
      setMonths(m.data.months || []);
      setGrand(m.data.grand || {});
      setVehicles(v.data);
    } catch (e) { toast.error(errMsg(e)); }
  };
  useEffect(() => { load(); }, []);

  const toggle = (m) => {
    const s = new Set(expanded);
    if (s.has(m)) s.delete(m); else s.add(m);
    setExpanded(s);
  };

  const openCreate = () => { setEditing(null); setForm(emptyRow()); setShowForm(true); };
  const openEdit = (r) => {
    setEditing(r);
    setForm({
      date: r.date, station: r.station || "", vehicle_id: r.vehicle_id || "",
      liters: r.liters, unit_price: r.unit_price, total: r.total,
    });
    setShowForm(true);
  };

  // Auto-calc total when liters+unit_price change
  const setF = (key, val) => {
    const f = { ...form, [key]: val };
    const liters = parseFloat(f.liters) || 0;
    const unit = parseFloat(f.unit_price) || 0;
    const total = parseFloat(f.total) || 0;
    if ((key === "liters" || key === "unit_price") && liters > 0 && unit > 0) {
      f.total = (liters * unit).toFixed(2);
    } else if (key === "total" && liters > 0 && total > 0) {
      f.unit_price = (total / liters).toFixed(3);
    }
    setForm(f);
  };

  const submit = async (e) => {
    e.preventDefault();
    const payload = {
      ...form,
      liters: Number(form.liters) || 0,
      unit_price: Number(form.unit_price) || 0,
      total: Number(form.total) || 0,
    };
    try {
      if (editing) await api.put(`/fuel-expenses/${editing.id}`, payload);
      else await api.post("/fuel-expenses", payload);
      toast.success("Kaydedildi");
      setShowForm(false); load();
    } catch (err) { toast.error(errMsg(err)); }
  };

  const remove = async (r) => {
    if (!window.confirm("Yakıt kaydını silmek istiyor musunuz?")) return;
    try { await api.delete(`/fuel-expenses/${r.id}`); toast.success("Silindi"); load(); }
    catch (err) { toast.error(errMsg(err)); }
  };

  const updateBulk = (i, key, val) => {
    const arr = [...bulkRows];
    arr[i] = { ...arr[i], [key]: val };
    const liters = parseFloat(arr[i].liters) || 0;
    const unit = parseFloat(arr[i].unit_price) || 0;
    if ((key === "liters" || key === "unit_price") && liters > 0 && unit > 0) {
      arr[i].total = (liters * unit).toFixed(2);
    }
    setBulkRows(arr);
  };

  const submitBulk = async () => {
    const valid = bulkRows.filter(r => r.date && (Number(r.liters) > 0 || Number(r.total) > 0));
    if (valid.length === 0) { toast.error("En az bir geçerli satır girin"); return; }
    try {
      const { data } = await api.post("/fuel-expenses/bulk", { rows: valid });
      toast.success(`${data.inserted} yakıt kaydı eklendi${data.skipped ? ` (${data.skipped} atlandı)` : ""}`);
      setShowBulk(false); setBulkRows([emptyRow()]); load();
    } catch (err) { toast.error(errMsg(err)); }
  };

  const uploadXlsx = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/fuel-expenses/upload-xlsx", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`${data.inserted} yakıt kaydı yüklendi${data.skipped ? ` (${data.skipped} atlandı)` : ""}`);
      load();
    } catch (err) { toast.error(errMsg(err)); }
  };

  const downloadTemplate = () => {
    const token = localStorage.getItem("token");
    window.open(`${API}/fuel-expenses/template/xlsx?auth=${token || ""}`, "_blank");
  };

  return (
    <>
      <PageHeader
        title="Yakıt Giderleri"
        subtitle="Aylık gruplanmış yakıt harcamaları. Toplu ekleme ve Excel yükleme desteklidir."
        testid="fuel-header"
        actions={
          <div className="flex items-center gap-2 flex-wrap">
            <button data-testid="fuel-template-btn" onClick={downloadTemplate} className="flex items-center gap-2 px-3 py-2.5 text-xs font-semibold uppercase tracking-[0.15em] border border-[#E5E5E5] hover:bg-[#F5F5F5]">
              <Download className="w-3.5 h-3.5" /> Boş Excel
            </button>
            <label className="flex items-center gap-2 px-3 py-2.5 text-xs font-semibold uppercase tracking-[0.15em] border border-[#E5E5E5] hover:bg-[#F5F5F5] cursor-pointer">
              <FileSpreadsheet className="w-3.5 h-3.5" /> <span data-testid="fuel-xlsx-upload">Excel Yükle</span>
              <input type="file" accept=".xlsx" className="hidden" onChange={(e) => { const f = e.target.files?.[0]; if (f) uploadXlsx(f); e.target.value = ""; }} />
            </label>
            <button data-testid="fuel-bulk-btn" onClick={() => { setBulkRows([emptyRow()]); setShowBulk(true); }} className="flex items-center gap-2 px-3 py-2.5 text-xs font-semibold uppercase tracking-[0.15em] border border-[#E5E5E5] hover:bg-[#F5F5F5]">
              <Upload className="w-3.5 h-3.5" /> Toplu Ekle
            </button>
            <button data-testid="fuel-add-btn" onClick={openCreate} className="flex items-center gap-2 bg-[#0A0A0A] text-white px-4 py-2.5 text-xs font-semibold uppercase tracking-[0.15em] hover:bg-[#0020C2] transition-colors">
              <Plus className="w-4 h-4" strokeWidth={2.2} /> Yeni Kayıt
            </button>
          </div>
        }
      />

      {/* Grand totals */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-6">
        <div className="bg-white border border-[#E5E5E5] p-4">
          <div className="text-[10px] uppercase tracking-widest text-neutral-500">Toplam Litre</div>
          <div className="num font-bold text-xl mt-1">{formatNumber(grand.total_liters || 0, 2)} L</div>
        </div>
        <div className="bg-white border border-[#E5E5E5] p-4">
          <div className="text-[10px] uppercase tracking-widest text-neutral-500">Toplam Gider</div>
          <div className="num font-bold text-xl mt-1">{formatTL(grand.total_cost || 0)}</div>
        </div>
        <div className="bg-white border border-[#E5E5E5] p-4">
          <div className="text-[10px] uppercase tracking-widest text-neutral-500">Ortalama Birim</div>
          <div className="num font-bold text-xl mt-1">{formatTL(grand.avg_unit_price || 0)}/L</div>
        </div>
        <div className="bg-white border border-[#E5E5E5] p-4">
          <div className="text-[10px] uppercase tracking-widest text-neutral-500">Kayıt Sayısı</div>
          <div className="num font-bold text-xl mt-1">{formatNumber(grand.count || 0)}</div>
        </div>
      </div>

      {/* Monthly groups */}
      <div className="space-y-3">
        {months.length === 0 ? (
          <div className="bg-white border border-[#E5E5E5] p-16 text-center text-sm text-neutral-500">
            <FuelIcon className="w-8 h-8 mx-auto mb-3 text-neutral-400" />
            Henüz yakıt kaydı yok. Yeni kayıt ekleyin veya Excel yükleyin.
          </div>
        ) : months.map(g => {
          const isOpen = expanded.has(g.month);
          return (
            <div key={g.month} className="bg-white border border-[#E5E5E5] rounded-md" data-testid={`fuel-month-${g.month}`}>
              <button onClick={() => toggle(g.month)} className="w-full px-5 py-4 flex items-center justify-between gap-3 text-left">
                <div className="flex items-center gap-3 min-w-0">
                  {isOpen ? <ChevronUp className="w-4 h-4 text-neutral-500 shrink-0" /> : <ChevronDown className="w-4 h-4 text-neutral-500 shrink-0" />}
                  <div>
                    <div className="font-heading font-bold text-lg">{monthLabel(g.month)}</div>
                    <div className="text-xs text-neutral-500 mt-0.5">
                      <span className="num font-semibold">{g.count}</span> kayıt · <span className="num">{formatNumber(g.total_liters, 2)}</span> L
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-6 flex-wrap">
                  <div className="text-right">
                    <div className="text-[10px] uppercase tracking-widest text-neutral-500">Toplam</div>
                    <div className="num font-bold text-base">{formatTL(g.total_cost)}</div>
                  </div>
                  <div className="text-right">
                    <div className="text-[10px] uppercase tracking-widest text-neutral-500">Ort. Birim</div>
                    <div className="num font-bold text-base text-[#0020C2]">{formatTL(g.avg_unit_price)}/L</div>
                  </div>
                </div>
              </button>
              {isOpen && (
                <div className="border-t border-[#F5F5F5] p-4 space-y-4">
                  {/* Per vehicle breakdown */}
                  {g.by_vehicle.length > 0 && (
                    <div>
                      <div className="text-[10px] uppercase tracking-widest text-neutral-500 mb-2 font-semibold">Araç Bazlı Kırılım</div>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                        {g.by_vehicle.map(v => (
                          <div key={v.vehicle_id || "novid"} className="border border-[#F5F5F5] p-2 text-xs bg-[#FAFAFA]">
                            <div className="font-semibold truncate">{v.vehicle_name || "Araçsız"}</div>
                            <div className="flex justify-between mt-1 text-neutral-600">
                              <span className="num">{formatNumber(v.total_liters, 1)} L</span>
                              <span className="num font-semibold">{formatTL(v.total_cost)}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  {/* Detailed list */}
                  <div className="overflow-x-auto">
                    <table className="w-full text-xs">
                      <thead className="bg-[#FAFAFA]">
                        <tr>
                          {["Tarih","İstasyon","Araç","Litre","Birim","Toplam",""].map((h, i) => (
                            <th key={h} className={`px-3 py-2 text-[10px] uppercase tracking-widest text-neutral-500 font-semibold ${i >= 3 && i <= 5 ? "text-right" : "text-left"}`}>{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {g.entries.map(r => (
                          <tr key={r.id} className="border-t border-[#F5F5F5]">
                            <td className="px-3 py-2 num">{formatDate(r.date)}</td>
                            <td className="px-3 py-2">{r.station || "-"}</td>
                            <td className="px-3 py-2 text-neutral-600">{r.vehicle_name || "-"}</td>
                            <td className="px-3 py-2 text-right num">{formatNumber(r.liters, 2)}</td>
                            <td className="px-3 py-2 text-right num">{formatTL(r.unit_price)}</td>
                            <td className="px-3 py-2 text-right num font-semibold">{formatTL(r.total)}</td>
                            <td className="px-3 py-2 text-right">
                              <div className="inline-flex gap-1">
                                <button onClick={() => openEdit(r)} className="p-1 hover:bg-[#F5F5F5]"><Pencil className="w-3 h-3" /></button>
                                <button onClick={() => remove(r)} className="p-1 hover:bg-[#DC2626] hover:text-white"><Trash2 className="w-3 h-3" /></button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Single form */}
      {showForm && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setShowForm(false)}>
          <div className="bg-white w-full max-w-2xl rounded-md shadow-lg" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between px-6 py-4 border-b border-[#E5E5E5]">
              <div>
                <div className="text-[10px] uppercase tracking-widest text-neutral-500">{editing ? "Düzenle" : "Yeni"}</div>
                <div className="font-heading font-bold text-lg">Yakıt Kaydı</div>
              </div>
              <button onClick={() => setShowForm(false)} className="p-1.5 hover:bg-[#F5F5F5]"><X className="w-4 h-4" /></button>
            </div>
            <form onSubmit={submit} className="p-6 grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="text-xs font-semibold uppercase tracking-widest text-neutral-600">Tarih *</label>
                <input type="date" required value={form.date} onChange={e => setF("date", e.target.value)} className="input mt-1.5 num" />
              </div>
              <div>
                <label className="text-xs font-semibold uppercase tracking-widest text-neutral-600">İstasyon</label>
                <input value={form.station} onChange={e => setF("station", e.target.value)} className="input mt-1.5" />
              </div>
              <div>
                <label className="text-xs font-semibold uppercase tracking-widest text-neutral-600">Araç</label>
                <select value={form.vehicle_id} onChange={e => setF("vehicle_id", e.target.value)} className="input mt-1.5">
                  <option value="">Seçiniz (opsiyonel)</option>
                  {vehicles.map(v => <option key={v.id} value={v.id}>{v.plate} - {v.model || ""}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs font-semibold uppercase tracking-widest text-neutral-600">Litre *</label>
                <input type="number" step="any" required value={form.liters} onChange={e => setF("liters", e.target.value)} className="input mt-1.5 num" />
              </div>
              <div>
                <label className="text-xs font-semibold uppercase tracking-widest text-neutral-600">Birim Fiyat (₺/L)</label>
                <input type="number" step="any" value={form.unit_price} onChange={e => setF("unit_price", e.target.value)} className="input mt-1.5 num" />
              </div>
              <div>
                <label className="text-xs font-semibold uppercase tracking-widest text-neutral-600">Toplam (₺) *</label>
                <input type="number" step="any" required value={form.total} onChange={e => setF("total", e.target.value)} className="input mt-1.5 num" />
              </div>
              <div className="md:col-span-2 flex justify-end gap-2 pt-4 border-t border-[#E5E5E5]">
                <button type="button" onClick={() => setShowForm(false)} className="px-4 py-2 text-xs uppercase tracking-widest font-semibold border border-[#E5E5E5] hover:bg-[#F5F5F5]">Vazgeç</button>
                <button type="submit" className="px-4 py-2 text-xs uppercase tracking-widest font-semibold bg-[#0A0A0A] text-white hover:bg-[#0020C2]">{editing ? "Kaydet" : "Oluştur"}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Bulk modal */}
      {showBulk && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setShowBulk(false)}>
          <div className="bg-white w-full max-w-5xl rounded-md shadow-lg max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between px-6 py-4 border-b border-[#E5E5E5] sticky top-0 bg-white z-10">
              <div>
                <div className="text-[10px] uppercase tracking-widest text-neutral-500">Toplu Yakıt Ekleme</div>
                <div className="font-heading font-bold text-lg">Çoklu Satır Girişi</div>
              </div>
              <button onClick={() => setShowBulk(false)} className="p-1.5 hover:bg-[#F5F5F5]"><X className="w-4 h-4" /></button>
            </div>
            <div className="p-6 space-y-4">
              <div className="flex items-center justify-between">
                <div className="text-xs text-neutral-500">Birim fiyat girildiğinde toplam otomatik hesaplanır.</div>
                <button onClick={() => setBulkRows([...bulkRows, emptyRow()])} className="flex items-center gap-1.5 px-2.5 py-1.5 text-[10px] uppercase tracking-widest font-semibold border border-[#E5E5E5] hover:bg-[#F5F5F5]">
                  <Plus className="w-3 h-3" /> Satır Ekle
                </button>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-xs border border-[#E5E5E5]">
                  <thead className="bg-[#FAFAFA]">
                    <tr>
                      {["Tarih *","İstasyon","Araç","Litre *","Birim (₺/L)","Toplam (₺)",""].map(h => (
                        <th key={h} className="px-2 py-2 text-left text-[10px] uppercase tracking-widest text-neutral-500 font-semibold">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {bulkRows.map((r, i) => (
                      <tr key={i} className="border-t border-[#F5F5F5]">
                        <td className="px-2 py-2">
                          <input data-testid={`bulk-fuel-date-${i}`} type="date" value={r.date} onChange={e => updateBulk(i, "date", e.target.value)} className="w-full px-2 py-1.5 bg-white border border-[#E5E5E5] text-xs num" />
                        </td>
                        <td className="px-2 py-2">
                          <input value={r.station} onChange={e => updateBulk(i, "station", e.target.value)} className="w-32 px-2 py-1.5 bg-white border border-[#E5E5E5] text-xs" />
                        </td>
                        <td className="px-2 py-2">
                          <select value={r.vehicle_id} onChange={e => updateBulk(i, "vehicle_id", e.target.value)} className="w-32 px-2 py-1.5 bg-white border border-[#E5E5E5] text-xs">
                            <option value="">-</option>
                            {vehicles.map(v => <option key={v.id} value={v.id}>{v.plate}</option>)}
                          </select>
                        </td>
                        <td className="px-2 py-2">
                          <input data-testid={`bulk-fuel-liters-${i}`} type="number" step="any" value={r.liters} onChange={e => updateBulk(i, "liters", e.target.value)} className="w-24 px-2 py-1.5 bg-white border border-[#E5E5E5] text-xs num text-right" />
                        </td>
                        <td className="px-2 py-2">
                          <input type="number" step="any" value={r.unit_price} onChange={e => updateBulk(i, "unit_price", e.target.value)} className="w-24 px-2 py-1.5 bg-white border border-[#E5E5E5] text-xs num text-right" />
                        </td>
                        <td className="px-2 py-2">
                          <input data-testid={`bulk-fuel-total-${i}`} type="number" step="any" value={r.total} onChange={e => updateBulk(i, "total", e.target.value)} className="w-28 px-2 py-1.5 bg-white border border-[#E5E5E5] text-xs num text-right" />
                        </td>
                        <td className="px-2 py-2">
                          {bulkRows.length > 1 && <button onClick={() => setBulkRows(bulkRows.filter((_, idx) => idx !== i))} className="p-1 hover:bg-[#DC2626] hover:text-white"><Trash2 className="w-3 h-3" /></button>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="flex justify-end gap-2 pt-4 border-t border-[#E5E5E5]">
                <button onClick={() => setShowBulk(false)} className="px-4 py-2 text-xs uppercase tracking-widest font-semibold border border-[#E5E5E5] hover:bg-[#F5F5F5]">Vazgeç</button>
                <button data-testid="bulk-fuel-submit" onClick={submitBulk} className="px-4 py-2 text-xs uppercase tracking-widest font-semibold bg-[#0A0A0A] text-white hover:bg-[#0020C2]">Toplu Kaydet</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
